import { IconSelector, IconX } from "@tabler/icons-react";
import clsx from "clsx";
import {
	type ClearIndicatorProps,
	type DropdownIndicatorProps,
	type MenuPlacement,
	type OptionProps,
	type SingleValue,
	components,
} from "react-select";
import CreatableSelectBase from "react-select/creatable";

type SelectOption = {
	value?: string | number | null;
	label: string;
};

type Props = {
	placeholder?: string;
	className?: string;
	clearable?: boolean;
	disabled?: boolean;
	options: SelectOption[];
	minHeight?: number;
	maxHeight?: number;
	onValueChange?: (value: SelectOption["value"]) => void;
	defaultValue?: SelectOption["value"];
	isValidNewOption?: (input: string) => boolean;
	formatCreateLabel?: (input: string) => string;
	onInputChange?: (input: string) => void;
};

const ClearIndicator = (props: ClearIndicatorProps<SelectOption, false>) => (
	<components.ClearIndicator {...props}>
		<IconX size={18} stroke={1.5} />
	</components.ClearIndicator>
);

const DropdownIndicator = (props: DropdownIndicatorProps<SelectOption, false>) => (
	<components.DropdownIndicator {...props}>
		<IconSelector size={18} stroke={1.5} />
	</components.DropdownIndicator>
);

const Option = (props: OptionProps<SelectOption, false>) => <components.Option {...props} />;

export default function CreatableSelect({
	placeholder,
	clearable = true,
	minHeight = 300,
	maxHeight = 400,
	...props
}: Props) {
	const currentValue =
		props.options.find((option) => option.value === props.defaultValue) ??
		(props.defaultValue != null && props.defaultValue !== ""
			? { value: props.defaultValue, label: String(props.defaultValue) }
			: null);

	return (
		<CreatableSelectBase
			key={String(props.defaultValue ?? "")}
			components={{ ClearIndicator, DropdownIndicator, Option }}
			placeholder={placeholder}
			value={currentValue}
			options={props.options}
			isClearable={clearable}
			isDisabled={props.disabled}
			minMenuHeight={minHeight}
			maxMenuHeight={maxHeight}
			menuPlacement={"auto" as MenuPlacement}
			isValidNewOption={props.isValidNewOption}
			formatCreateLabel={props.formatCreateLabel}
			onInputChange={(input, meta) => {
				if (meta.action === "input-change" && props.onInputChange) {
					props.onInputChange(input);
				}
			}}
			onChange={(option: SingleValue<SelectOption>) => {
				if (props.onValueChange) {
					props.onValueChange(option?.value ?? null);
				}
			}}
			classNames={{
				control: ({ isDisabled }) =>
					clsx(
						"flex flex-row w-full min-h-[38px] max-h-[38px] h-[38px] pl-3 pr-0 gap-3 text-base !rounded-md",
						isDisabled
							? "!bg-muted !border-border !text-muted-foreground !cursor-not-allowed"
							: "!bg-background !border-border focus-within:!border-focus-border focus-within:!ring-2 focus-within:!ring-focus-ring/30 !ring-offset-0 !outline-0",
					),
				valueContainer: () => "!p-0",
				placeholder: () => "!text-muted-foreground",
				singleValue: ({ isDisabled }) => clsx(isDisabled ? "!text-muted-foreground" : "!text-foreground"),
				menu: () => "!mt-1 !bg-background !border-1 !border-border !rounded-md !z-[90]",
				option: ({ isFocused, isSelected }) =>
					clsx(
						"!px-3 !py-2 !text-base !text-foreground !cursor-pointer",
						isSelected ? "!bg-blue-100 !text-foreground" : isFocused ? "!bg-blue-50" : "!text-foreground",
					),
				dropdownIndicator: () => "!text-muted-foreground",
				clearIndicator: () => "!text-muted-foreground !cursor-pointer",
			}}
			className={clsx("w-full text-base", props.className)}
		/>
	);
}
